import React, { useState, useEffect } from 'react';
import { X, Play, Pause, Scissors } from 'lucide-react';

const AudioTrimDialog = ({ isOpen, onClose, audioItem, onSave }) => {
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioElement, setAudioElement] = useState(null);

  // Initialize trim values when dialog opens
  useEffect(() => {
    if (isOpen && audioItem) {
      const originalDuration = audioItem.file?.duration || audioItem.duration || 5;
      setTrimStart(audioItem.trim?.start || 0);
      setTrimEnd(audioItem.trim?.end || originalDuration);
      setCurrentTime(audioItem.trim?.start || 0);
    }
  }, [isOpen, audioItem]);

  // Audio element setup
  useEffect(() => {
    if (isOpen && audioItem) {
      const audio = new Audio();
      audio.src = audioItem.file?.url || `/api/v1/audio/${audioItem.id}/stream`;
      audio.preload = 'metadata';
      
      audio.addEventListener('loadedmetadata', () => {
        console.log('🎵 Audio loaded, duration:', audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
        
        // Stop at trim end
        if (audio.currentTime >= trimEnd) {
          audio.pause();
          setIsPlaying(false);
          audio.currentTime = trimStart;
        }
      });
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        audio.currentTime = trimStart;
      });
      
      setAudioElement(audio);
      
      return () => {
        audio.pause();
        audio.src = '';
        setAudioElement(null);
      };
    }
  }, [isOpen, audioItem, trimEnd, trimStart]);

  const handlePlay = async () => {
    if (!audioElement) return;
    
    try {
      if (isPlaying) {
        audioElement.pause();
        setIsPlaying(false);
      } else {
        // Start from trim start if not already in range
        if (audioElement.currentTime < trimStart || audioElement.currentTime >= trimEnd) {
          audioElement.currentTime = trimStart;
        }
        await audioElement.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('❌ Error playing audio:', error);
    }
  };

  const handleSeek = (time) => {
    if (audioElement) {
      const clampedTime = Math.max(trimStart, Math.min(trimEnd, time));
      audioElement.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    }
  };

  const handleSave = () => {
    const trimmedDuration = trimEnd - trimStart;
    const updatedAudioItem = {
      ...audioItem,
      trim: {
        start: trimStart,
        end: trimEnd
      },
      // Update time.duration to reflect trimmed length (drives timeline width)
      time: {
        ...(audioItem.time || {}),
        duration: trimmedDuration
      }
    };
    
    console.log('🎵 Saving trimmed audio:', {
      original: audioItem.duration,
      trimStart,
      trimEnd,
      newDuration: trimmedDuration
    });
    
    onSave(updatedAudioItem);
    onClose();
  };

  if (!isOpen || !audioItem) return null;

  const originalDuration = audioItem.file?.duration || audioItem.duration || 5;
  const trimmedDuration = trimEnd - trimStart;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Trim Audio</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Audio Info */}
          <div className="text-sm text-gray-600">
            <p><strong>Audio:</strong> {audioItem.name || 'Untitled'}</p>
            <p><strong>Original Duration:</strong> {originalDuration.toFixed(2)}s</p>
            <p><strong>Trimmed Duration:</strong> {trimmedDuration.toFixed(2)}s</p>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePlay}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <span className="text-sm text-gray-600">
              {currentTime.toFixed(2)}s / {originalDuration.toFixed(2)}s
            </span>
          </div>

          {/* Waveform/Timeline Visualization */}
          <div className="relative">
            <div className="h-12 bg-gray-100 rounded relative overflow-hidden">
              {/* Trim range indicator */}
              <div
                className="absolute top-0 bottom-0 bg-blue-200 border-l-2 border-r-2 border-blue-500"
                style={{
                  left: `${(trimStart / originalDuration) * 100}%`,
                  width: `${((trimEnd - trimStart) / originalDuration) * 100}%`
                }}
              />
              
              {/* Current time indicator */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                style={{ left: `${(currentTime / originalDuration) * 100}%` }}
              />
              
              {/* Clickable timeline */}
              <div
                className="absolute inset-0 cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const time = (x / rect.width) * originalDuration;
                  handleSeek(time);
                }}
              />
            </div>
            
            {/* Time markers */}
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0s</span>
              <span>{originalDuration.toFixed(1)}s</span>
            </div>
          </div>

          {/* Trim Controls */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trim Start (seconds)
              </label>
              <input
                type="number"
                min="0"
                max={trimEnd - 0.1}
                step="0.1"
                value={trimStart.toFixed(1)}
                onChange={(e) => setTrimStart(Math.max(0, Math.min(trimEnd - 0.1, parseFloat(e.target.value) || 0)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trim End (seconds)
              </label>
              <input
                type="number"
                min={trimStart + 0.1}
                max={originalDuration}
                step="0.1"
                value={trimEnd.toFixed(1)}
                onChange={(e) => setTrimEnd(Math.max(trimStart + 0.1, Math.min(originalDuration, parseFloat(e.target.value) || originalDuration)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setTrimStart(0);
                setTrimEnd(originalDuration);
              }}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={() => {
                const quarter = originalDuration / 4;
                setTrimStart(quarter);
                setTrimEnd(originalDuration - quarter);
              }}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Trim 25%
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Apply Trim
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioTrimDialog;
